import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Phone, Mail, Undo2 } from 'lucide-react';

// Owner dashboard: deposits owed back to customers after we cancelled.
export const DepositRefunds = ({ authFetch }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => authFetch('get', '/admin/refunds').then(({ data }) => setD(data)).catch(() => setD({ pending: [], refunded: [] })), [authFetch]);
  useEffect(() => { load(); }, [load]);
  const done = async (r) => {
    const note = window.prompt(`Mark £${r.amount} refunded to ${r.name}?${r.email ? ' They will get a "deposit refunded" email.' : ''} Optional note (e.g. how it was paid):`, '');
    if (note === null) return;
    try {
      const { data } = await authFetch('post', `/admin/refunds/${r.id}/mark-refunded`, { note });
      toast.success(data.emailed ? 'Marked as refunded. Customer emailed.' : data.has_email ? "Marked as refunded, but the email didn't send. Please let the customer know." : 'Marked as refunded. No email on file.');
      load();
    }
    catch { toast.error("Couldn't update."); }
  };
  if (!d) return null;
  return (
    <section id="sec-deposit-refunds" className="mb-8 scroll-mt-28 rounded-xl border-2 border-red-200 bg-white p-4" data-testid="deposit-refunds">
      <div className="flex items-center gap-2 mb-2">
        <Undo2 className="w-4 h-4 text-red-600" />
        <h2 className="font-display font-black text-[color:var(--mv-navy)]">Deposit refunds</h2>
        <span className="text-xs text-slate-500" data-testid="deposit-refunds-count">{d.pending.length} to refund</span>
      </div>
      {d.pending.length === 0 ? <div className="text-sm text-slate-500">No deposits waiting to be refunded.</div> : (
        <div className="divide-y divide-slate-100">
          {d.pending.map(r => (
            <div key={r.id} className="py-3 flex flex-wrap items-start gap-x-6 gap-y-2 text-sm" data-testid={`refund-${r.ref}`}>
              <div className="min-w-[170px]">
                <div className="font-bold text-[color:var(--mv-navy)]">{r.name} <span className="text-[10px] text-slate-400">#{r.ref}</span></div>
                {r.phone && <a href={`tel:${r.phone}`} className="text-xs text-slate-600 inline-flex items-center gap-1"><Phone className="w-3 h-3" />{r.phone}</a>}
                {r.email && <a href={`mailto:${r.email}`} className="block text-xs text-slate-600 inline-flex items-center gap-1"><Mail className="w-3 h-3" />{r.email}</a>}
              </div>
              <div><div className="text-[10px] uppercase font-bold text-slate-500">Refund</div><div className="font-black text-red-700" data-testid={`refund-amount-${r.ref}`}>£{r.amount}</div></div>
              <div><div className="text-[10px] uppercase font-bold text-slate-500">Was booked</div><div>{r.booking_date || '—'} {r.booking_time || ''}</div></div>
              <div><div className="text-[10px] uppercase font-bold text-slate-500">Their payment ref</div><div className="font-mono">{r.deposit_reference || '—'}</div>{!r.deposit_checked && <div className="text-[10px] text-amber-700 font-bold">Deposit never checked in Tide</div>}</div>
              <div className="flex-1 min-w-[160px]"><div className="text-[10px] uppercase font-bold text-slate-500">Why we cancelled</div><div>{r.reason}</div>{r.cancelled_by && <div className="text-[10px] text-slate-400">by {r.cancelled_by}</div>}</div>
              <button type="button" onClick={() => done(r)} className="h-9 px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold" data-testid={`refund-done-${r.ref}`}>Mark refunded</button>
            </div>
          ))}
        </div>
      )}
      {d.refunded.length > 0 && (
        <details className="mt-3 text-xs text-slate-500"><summary className="cursor-pointer font-bold">Recently refunded ({d.refunded.length})</summary>
          <ul className="mt-1 space-y-0.5">{d.refunded.map(r => <li key={r.id}>{r.name} #{r.ref} · £{r.amount} · {r.refunded_at ? new Date(r.refunded_at).toLocaleDateString('en-GB') : ''}{r.refund_note ? ` · ${r.refund_note}` : ''}</li>)}</ul>
        </details>
      )}
    </section>
  );
};
